<template>
  <div class="modal show">
    <div class="modal-content">
      <span class="close" @click="$emit('close')">&times;</span>
      <h2>{{ t('createVM.title') }}</h2>

      <form @submit.prevent="handleSubmit">
        <div class="form-group">
          <label for="vmName">{{ t('createVM.name') }}:</label>
          <input
            type="text"
            id="vmName"
            v-model="formData.instanceName"
            required
          >
        </div>

        <div class="form-group">
          <label for="vmZone">{{ currentProvider === 'aws' ? t('createVM.region') : t('createVM.zone') }}:</label>
          <select id="vmZone" v-model="formData.zone" required>
            <option value="">{{ t('common.loading') }}</option>
            <option v-if="currentProvider === 'gcp'" value="asia-northeast1-a">asia-northeast1-a</option>
            <option v-if="currentProvider === 'gcp'" value="asia-northeast1-b">asia-northeast1-b</option>
            <option v-if="currentProvider === 'gcp'" value="asia-northeast1-c">asia-northeast1-c</option>
            <option v-if="currentProvider === 'aws'" value="ap-northeast-1a">ap-northeast-1a</option>
            <option v-if="currentProvider === 'aws'" value="ap-northeast-1c">ap-northeast-1c</option>
            <option v-if="currentProvider === 'aws'" value="ap-northeast-1d">ap-northeast-1d</option>
          </select>
        </div>

        <!-- GPU选择 -->
        <div v-if="currentProvider === 'gcp'" class="form-group">
          <label for="gpuType">{{ t('createVM.gpuType') }}:</label>
          <select id="gpuType" v-model="formData.gpuType" @change="updateAvailableMachineTypes">
            <option value="">{{ t('createVM.gpuTypes.none') }}</option>
            <option value="nvidia-l4">NVIDIA L4</option>
            <option value="nvidia-tesla-t4">NVIDIA T4</option>
            <option value="nvidia-tesla-a100">NVIDIA A100 40G</option>
          </select>
        </div>

        <!-- GPU数量选择 -->
        <div v-if="formData.gpuType" class="form-group">
          <label for="gpuCount">{{ t('createVM.gpuCount') }}:</label>
          <select id="gpuCount" v-model.number="formData.gpuCount" @change="updateAvailableMachineTypes">
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="4">4</option>
          </select>
        </div>

        <div class="form-group">
          <label for="vmMachineType">{{ currentProvider === 'aws' ? t('createVM.instanceType') : t('createVM.machineType') }}:</label>
          <select id="vmMachineType" v-model="formData.machineType" required @change="updateCostEstimate" v-if="currentProvider === 'gcp' && formData.gpuType !== 'nvidia-tesla-a100'">
              <option value="">{{ t('createVM.machineTypeRequired') }}</option>
              <optgroup v-if="!formData.gpuType" :label="t('createVM.machineTypeGroups.e2Series')">
              <option v-for="type in availableE2Types" :key="type.value" :value="type.value">
                {{ type.label }} - {{ type.spec }}
              </option>
            </optgroup>
              <optgroup v-if="!formData.gpuType" :label="t('createVM.machineTypeGroups.e2Standard')">
              <option v-for="type in availableE2StandardTypes" :key="type.value" :value="type.value">
                {{ type.label }} - {{ type.spec }}
              </option>
            </optgroup>
              <optgroup v-if="!formData.gpuType" :label="t('createVM.machineTypeGroups.e2HighCpu')">
              <option v-for="type in availableE2HighCpuTypes" :key="type.value" :value="type.value">
                {{ type.label }} - {{ type.spec }}
              </option>
            </optgroup>
              <optgroup v-if="!formData.gpuType" :label="t('createVM.machineTypeGroups.n1Standard')">
              <option v-for="type in availableN1StandardTypes" :key="type.value" :value="type.value">
                {{ type.label }} - {{ type.spec }}
              </option>
            </optgroup>
              <optgroup v-if="formData.gpuType === 'nvidia-l4'" :label="t('createVM.machineTypeGroups.g2StandardL4')">
              <option v-for="type in availableG2Types" :key="type.value" :value="type.value">
                {{ type.label }} - {{ type.spec }}
              </option>
            </optgroup>
              <optgroup v-if="formData.gpuType === 'nvidia-tesla-t4'" :label="t('createVM.machineTypeGroups.n1StandardT4')">
              <option v-for="type in availableN1WithT4Types" :key="type.value" :value="type.value">
                {{ type.label }} - {{ type.spec }}
              </option>
            </optgroup>
          </select>

          <!-- AWS Instance Type Selection -->
          <select id="vmInstanceType" v-model="formData.machineType" required @change="updateCostEstimate" v-if="currentProvider === 'aws'">
              <option value="">{{ t('createVM.instanceTypeRequired') }}</option>
              <optgroup label="T2 Series (Burstable Performance)">
              <option v-for="type in availableT2Types" :key="type.value" :value="type.value">
                {{ type.label }} - {{ type.spec }}
              </option>
            </optgroup>
              <optgroup label="M5 Series (General Purpose)">
              <option v-for="type in availableM5Types" :key="type.value" :value="type.value">
                {{ type.label }} - {{ type.spec }}
              </option>
            </optgroup>
              <optgroup label="C5 Series (Compute Optimized)">
              <option v-for="type in availableC5Types" :key="type.value" :value="type.value">
                {{ type.label }} - {{ type.spec }}
              </option>
            </optgroup>
              <optgroup label="G4 Series (GPU Instances)">
              <option v-for="type in availableG4Types" :key="type.value" :value="type.value">
                {{ type.label }} - {{ type.spec }}
              </option>
            </optgroup>
              <optgroup label="P3 Series (GPU Instances)">
              <option v-for="type in availableP3Types" :key="type.value" :value="type.value">
                {{ type.label }} - {{ type.spec }}
              </option>
            </optgroup>
          </select>

          <!-- A100 GPU时显示自动选择的机器类型 -->
          <div v-if="formData.gpuType === 'nvidia-tesla-a100'" class="auto-machine-type">
            <div class="machine-type-display">
              <span class="machine-type-label">{{ t('createVM.machineType') }}:</span>
              <span class="machine-type-value">
                                  {{ autoSelectedA2Type ? (autoSelectedA2Type.label + ' - ' + autoSelectedA2Type.spec) : t('createVM.machineTypeRequired') }}
              </span>
            </div>
            <div class="machine-type-note">
              <i class="fas fa-info-circle"></i>
              <span>{{ t('createVM.a100AutoMatch') }}</span>
            </div>
          </div>
        </div>

        <!-- 成本估算显示 -->
        <CostEstimateDisplay
          v-if="formData.machineType"
          :monthlyCost="monthlyCost"
          :gpuType="formData.gpuType"
          :gpuCount="formData.gpuCount"
          :diskSizeGb="formData.diskSizeGb"
        />

        <div class="form-group">
          <label for="vmImage">{{ t('createVM.image') }}:</label>
          <select id="vmImage" v-model="formData.sourceImage" required>
            <option value="">{{ t('createVM.selectImage') }}</option>
            <option v-if="currentProvider === 'gcp'" value="projects/ubuntu-os-cloud/global/images/family/ubuntu-2204-lts">Ubuntu 22.04 LTS</option>
            <option v-if="currentProvider === 'aws'" value="ami-0d88b56ff2c65082e">Ubuntu Server 22.04 LTS</option>
          </select>
        </div>

        <div class="form-group">
          <label for="vmDiskSize">{{ t('createVM.bootDiskSize') }}:</label>
          <div class="slider-input-group">
            <input
              type="range"
              id="vmDiskSizeSlider"
              v-model.number="formData.diskSizeGb"
              min="10"
              max="512"
              class="slider"
              @input="updateCostEstimate"
            >
            <input
              type="number"
              id="vmDiskSize"
              v-model.number="formData.diskSizeGb"
              min="10"
              max="512"
              class="number-input"
              required
              @input="updateCostEstimate"
            >
          </div>
        </div>

        <!-- AWS Volume Type Selection -->
        <div v-if="currentProvider === 'aws'" class="form-group">
          <label for="volumeType">{{ t('createVM.volumeType') }}:</label>
          <select id="volumeType" v-model="formData.volumeType">
            <option value="gp2">General Purpose SSD (gp2)</option>
            <option value="gp3">General Purpose SSD (gp3)</option>
            <option value="io1">Provisioned IOPS SSD (io1)</option>
            <option value="io2">Provisioned IOPS SSD (io2)</option>
          </select>
        </div>

        <div v-if="currentProvider === 'gcp'" class="form-group">
          <label for="serviceAccount">{{ t('createVM.serviceAccount') }}:</label>
          <select id="serviceAccount" v-model="formData.serviceAccount">
            <option value="">{{ t('createVM.defaultServiceAccount') }}</option>
            <option v-for="sa in serviceAccounts" :key="sa" :value="sa">{{ sa }}</option>
          </select>
        </div>


        <div class="form-actions">
          <button type="submit" class="btn btn-success" :disabled="loading">
            {{ loading ? t('createVM.creating') : t('createVM.create') }}
          </button>
          <button type="button" class="btn btn-secondary" @click="$emit('close')">
            {{ t('createVM.cancel') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, nextTick, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useResources } from '../composables/useResources';
import { useCostEstimation } from '../composables/useCostEstimation';
import { useCloudProvider } from '../composables/useCloudProvider.js';
import CostEstimateDisplay from './CostEstimateDisplay.vue';

export default {
  name: 'CreateVMModal',
  components: {
    CostEstimateDisplay
  },
  emits: ['close', 'created'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const { createVM, currentProjectId } = useResources();
    const { monthlyCost, calculateCost } = useCostEstimation();
    const { currentProvider } = useCloudProvider();
    const loading = ref(false);

    const formData = reactive({
      instanceName: '',
      zone: '',
      machineType: '',
      sourceImage: '',
      diskSizeGb: 10,
      gpuType: '',
      gpuCount: 1,
      serviceAccount: '',
      // AWS specific fields
      instanceType: '',
      volumeType: 'gp2',
      volumeSize: 10
    });

    // GCP VM type definitions
    const e2Types = [
      { value: 'e2-micro', label: 'e2-micro', spec: '0.5-2 vCPU, 1 GB RAM' },
      { value: 'e2-small', label: 'e2-small', spec: '0.5-2 vCPU, 2 GB RAM' },
      { value: 'e2-medium', label: 'e2-medium', spec: '1-2 vCPU, 4 GB RAM' }
    ];

    const e2StandardTypes = [
      { value: 'e2-standard-16', label: 'e2-standard-16', spec: '16 vCPU, 64 GB RAM' },
      { value: 'e2-standard-32', label: 'e2-standard-32', spec: '32 vCPU, 128 GB RAM' }
    ];

    const e2HighCpuTypes = [
      { value: 'e2-highcpu-16', label: 'e2-highcpu-16', spec: '16 vCPU, 16 GB RAM' },
      { value: 'e2-highcpu-32', label: 'e2-highcpu-32', spec: '32 vCPU, 32 GB RAM' }
    ];

    const n1StandardTypes = [
      { value: 'n1-standard-16', label: 'n1-standard-16', spec: '16 vCPU, 60 GB RAM' },
      { value: 'n1-standard-32', label: 'n1-standard-32', spec: '32 vCPU, 120 GB RAM' }
    ];

    // G2 series (L4 GPU)
    const g2StandardTypes = [
      { value: 'g2-standard-16', label: 'g2-standard-16', spec: '16 vCPU, 64 GB RAM' },
      { value: 'g2-standard-32', label: 'g2-standard-32', spec: '32 vCPU, 128 GB RAM' }
    ];

    // A2 series (A100 GPU)
    const a2HighGpuTypes = [
      { value: 'a2-highgpu-1g', label: 'a2-highgpu-1g', spec: '12 vCPU, 85 GB RAM' },
      { value: 'a2-highgpu-2g', label: 'a2-highgpu-2g', spec: '24 vCPU, 170 GB RAM' },
      { value: 'a2-highgpu-4g', label: 'a2-highgpu-4g', spec: '48 vCPU, 340 GB RAM' }
    ];

    // AWS instance type definitions
    const t2Types = [
      { value: 't2.micro', label: 't2.micro', spec: '1 vCPU, 1 GB RAM' },
      { value: 't2.small', label: 't2.small', spec: '1 vCPU, 2 GB RAM' },
      { value: 't2.medium', label: 't2.medium', spec: '2 vCPU, 4 GB RAM' },
      { value: 't2.large', label: 't2.large', spec: '2 vCPU, 8 GB RAM' },
      { value: 't2.xlarge', label: 't2.xlarge', spec: '4 vCPU, 16 GB RAM' },
      { value: 't2.2xlarge', label: 't2.2xlarge', spec: '8 vCPU, 32 GB RAM' }
    ];

    const m5Types = [
      { value: 'm5.large', label: 'm5.large', spec: '2 vCPU, 8 GB RAM' },
      { value: 'm5.xlarge', label: 'm5.xlarge', spec: '4 vCPU, 16 GB RAM' },
      { value: 'm5.2xlarge', label: 'm5.2xlarge', spec: '8 vCPU, 32 GB RAM' },
      { value: 'm5.4xlarge', label: 'm5.4xlarge', spec: '16 vCPU, 64 GB RAM' },
      { value: 'm5.8xlarge', label: 'm5.8xlarge', spec: '32 vCPU, 128 GB RAM' },
      { value: 'm5.12xlarge', label: 'm5.12xlarge', spec: '48 vCPU, 192 GB RAM' },
      { value: 'm5.16xlarge', label: 'm5.16xlarge', spec: '64 vCPU, 256 GB RAM' },
      { value: 'm5.24xlarge', label: 'm5.24xlarge', spec: '96 vCPU, 384 GB RAM' }
    ];

    const c5Types = [
      { value: 'c5.large', label: 'c5.large', spec: '2 vCPU, 4 GB RAM' },
      { value: 'c5.xlarge', label: 'c5.xlarge', spec: '4 vCPU, 8 GB RAM' },
      { value: 'c5.2xlarge', label: 'c5.2xlarge', spec: '8 vCPU, 16 GB RAM' },
      { value: 'c5.4xlarge', label: 'c5.4xlarge', spec: '16 vCPU, 32 GB RAM' },
      { value: 'c5.9xlarge', label: 'c5.9xlarge', spec: '36 vCPU, 72 GB RAM' },
      { value: 'c5.12xlarge', label: 'c5.12xlarge', spec: '48 vCPU, 96 GB RAM' },
      { value: 'c5.18xlarge', label: 'c5.18xlarge', spec: '72 vCPU, 144 GB RAM' },
      { value: 'c5.24xlarge', label: 'c5.24xlarge', spec: '96 vCPU, 192 GB RAM' }
    ];

    const g4Types = [
      { value: 'g4dn.xlarge', label: 'g4dn.xlarge', spec: '4 vCPU, 16 GB RAM, 1x T4 GPU' },
      { value: 'g4dn.2xlarge', label: 'g4dn.2xlarge', spec: '8 vCPU, 32 GB RAM, 1x T4 GPU' },
      { value: 'g4dn.4xlarge', label: 'g4dn.4xlarge', spec: '16 vCPU, 64 GB RAM, 1x T4 GPU' },
      { value: 'g4dn.8xlarge', label: 'g4dn.8xlarge', spec: '32 vCPU, 128 GB RAM, 1x T4 GPU' },
      { value: 'g4dn.12xlarge', label: 'g4dn.12xlarge', spec: '48 vCPU, 192 GB RAM, 4x T4 GPU' },
      { value: 'g4dn.16xlarge', label: 'g4dn.16xlarge', spec: '64 vCPU, 256 GB RAM, 1x T4 GPU' }
    ];

    const p3Types = [
      { value: 'p3.2xlarge', label: 'p3.2xlarge', spec: '8 vCPU, 61 GB RAM, 1x V100 GPU' },
      { value: 'p3.8xlarge', label: 'p3.8xlarge', spec: '32 vCPU, 244 GB RAM, 4x V100 GPU' },
      { value: 'p3.16xlarge', label: 'p3.16xlarge', spec: '64 vCPU, 488 GB RAM, 8x V100 GPU' }
    ];

    // Available machine types (dynamically adjusted based on provider and GPU selection)
    const availableE2Types = ref([]);
    const availableE2StandardTypes = ref([]);
    const availableE2HighCpuTypes = ref([]);
    const availableN1StandardTypes = ref([]);
    const availableG2Types = ref([]);
    const availableN1WithT4Types = ref([]);
    const availableA2Types = ref([]);

    // AWS instance types
    const availableT2Types = ref([]);
    const availableM5Types = ref([]);
    const availableC5Types = ref([]);
    const availableG4Types = ref([]);
    const availableP3Types = ref([]);

    // Initialize AWS instance types immediately
    if (currentProvider.value === 'aws') {
      availableT2Types.value = [...t2Types];
      availableM5Types.value = [...m5Types];
      availableC5Types.value = [...c5Types];
      availableG4Types.value = [...g4Types];
      availableP3Types.value = [...p3Types];
    }


    const selectedMachineType = computed(() => {
      return formData.machineType;
    });

    // Auto-selected A2 machine type (based on GPU count)
    const autoSelectedA2Type = computed(() => {
      if (formData.gpuType === 'nvidia-tesla-a100' && formData.gpuCount) {
        const gpuCount = Number(formData.gpuCount);
        return a2HighGpuTypes.find(type => {
          const match = type.value.match(/-(\d+)g$/);
          return match && Number(match[1]) === gpuCount;
        }) || null;
      }
      return null;
    });


    // Listen for changes in GPU type and count, automatically update price
    watch([
      () => formData.gpuType,
      () => formData.gpuCount
    ], () => {
      if (formData.gpuType === 'nvidia-tesla-a100' && autoSelectedA2Type.value) {
        formData.machineType = autoSelectedA2Type.value.value;
        nextTick(() => {
          updateCostEstimate();
        });
      } else if (formData.gpuType === 'nvidia-tesla-a100' && !autoSelectedA2Type.value) {
        formData.machineType = '';
        updateCostEstimate();
      } else {
        updateCostEstimate();
      }
    });

    // Watch for provider changes
    watch(currentProvider, () => {
      updateAvailableMachineTypes();
    });

    const updateAvailableMachineTypes = () => {
      formData.machineType = '';

      if (currentProvider.value === 'aws') {
        // AWS instance types
        availableT2Types.value = [...t2Types];
        availableM5Types.value = [...m5Types];
        availableC5Types.value = [...c5Types];
        availableG4Types.value = [...g4Types];
        availableP3Types.value = [...p3Types];
      } else {
        // GCP machine types
        if (!formData.gpuType) {
          availableE2Types.value = [...e2Types];
          availableE2StandardTypes.value = [...e2StandardTypes];
          availableE2HighCpuTypes.value = [...e2HighCpuTypes];
          availableN1StandardTypes.value = [...n1StandardTypes];
          availableG2Types.value = [];
          availableN1WithT4Types.value = [];
          availableA2Types.value = [];
        } else if (formData.gpuType === 'nvidia-l4') {
          availableE2Types.value = [];
          availableE2StandardTypes.value = [];
          availableE2HighCpuTypes.value = [];
          availableN1StandardTypes.value = [];
          availableG2Types.value = [...g2StandardTypes];
          availableN1WithT4Types.value = [];
          availableA2Types.value = [];
        } else if (formData.gpuType === 'nvidia-tesla-t4') {
          availableE2Types.value = [];
          availableE2StandardTypes.value = [];
          availableE2HighCpuTypes.value = [];
          availableN1StandardTypes.value = [...n1StandardTypes];
          availableG2Types.value = [];
          availableN1WithT4Types.value = [...n1StandardTypes];
          availableA2Types.value = [];
        } else if (formData.gpuType === 'nvidia-tesla-a100') {
          availableE2Types.value = [];
          availableE2StandardTypes.value = [];
          availableE2HighCpuTypes.value = [];
          availableN1StandardTypes.value = [];
          availableG2Types.value = [];
          availableN1WithT4Types.value = [];
          availableA2Types.value = [];
          if (autoSelectedA2Type.value) {
            formData.machineType = autoSelectedA2Type.value.value;
            nextTick(() => {
              updateCostEstimate();
            });
          }
        }
      }
      updateCostEstimate();
    };

    const updateCostEstimate = () => {
      calculateCost(formData.machineType, formData.gpuType, formData.gpuCount, formData.diskSizeGb, currentProvider.value);
    };

    const handleSubmit = async () => {
      loading.value = true;
      try {
        formData.projectId = currentProjectId.value;
        await createVM(formData);
        emit('created');
      } catch (error) {
        // Friendly display of GCP error information returned from backend
        console.log('error:', error);
        let msg = t('createVM.createFailed');
        // Compatible with fetch/axios/backend json multiple styles
        const details = error?.response?.data?.details || error?.details || error?.data?.details;
        if (details && details.errors && details.errors.length > 0) {
          msg = details.errors[0].message || msg;
          // 如果有localizedMessage，追加显示
          const localized = (details.errors[0].errorDetails || []).find(
            d => d.localizedMessage && d.localizedMessage.message
          );
          if (localized) {
            msg += `\n${localized.localizedMessage.message}`;
          }
        } else if (error?.response?.data?.error) {
          msg = error.response.data.error;
        } else if (error?.error) {
          msg = error.error;
        } else if (typeof error === 'string') {
          msg = error;
        } else if (error.message) {
          msg = error.message;
        } else if (typeof error === 'object') {
          msg = JSON.stringify(error);
        }
        alert(msg);
      } finally {
        loading.value = false;
      }
    };

    const serviceAccounts = ref([]);
    const serviceAccountsLoading = ref(false);

    // 加载Service Account列表
    const loadServiceAccounts = async () => {
      if (!currentProjectId.value) {return;}
      serviceAccountsLoading.value = true;
      try {
        const res = await fetch(`/api/service-accounts?projectId=${currentProjectId.value}`);
        const data = await res.json();
        serviceAccounts.value = data.accounts || [];
      } catch {
        serviceAccounts.value = [];
      } finally {
        serviceAccountsLoading.value = false;
      }
    };

    onMounted(() => {
      // Set default zone/region based on provider
      if (currentProvider.value === 'aws') {
        formData.zone = 'ap-northeast-1a';
      } else {
        formData.zone = 'asia-northeast1-a';
      }

      if (currentProvider.value === 'gcp') {
        loadServiceAccounts();
      }
      updateAvailableMachineTypes();
    });

    return {
      formData,
      loading,
      selectedMachineType,
      autoSelectedA2Type,
      monthlyCost,
      handleSubmit,
      updateCostEstimate,
      updateAvailableMachineTypes,
      availableE2Types,
      availableE2StandardTypes,
      availableE2HighCpuTypes,
      availableN1StandardTypes,
      availableG2Types,
      availableN1WithT4Types,
      availableA2Types,
      availableT2Types,
      availableM5Types,
      availableC5Types,
      availableG4Types,
      availableP3Types,
      serviceAccounts,
      serviceAccountsLoading,
      loadServiceAccounts,
      currentProvider,
      t
    };
  }
};
</script>

<style scoped>
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  padding: 24px;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
}

.close {
  position: absolute;
  top: 16px;
  right: 20px;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  z-index: 1;
}

.close:hover {
  color: #333;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #333;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #eee;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-success {
  background: #28a745;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background: #218838;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #5a6268;
}

.slider-input-group {
  display: flex;
  align-items: center;
  gap: 10px;
}
.slider {
  flex: 1;
  min-width: 200px;
  width: 100%;
  max-width: 600px;
  margin-right: 10px;
}
.number-input {
  width: 40px;
}


select optgroup {
  font-weight: 600;
  color: #495057;
}

select option {
  font-weight: normal;
  color: #212529;
}

.auto-machine-type {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 12px;
  background: #f8f9fa;
}

.machine-type-display {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.machine-type-label {
  font-weight: 600;
  color: #495057;
  min-width: 80px;
}

.machine-type-value {
  color: #28a745;
  font-weight: 500;
}

.machine-type-note {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: #6c757d;
}

.machine-type-note i {
  color: #17a2b8;
}
</style>
